import { createSlice } from '@reduxjs/toolkit';
import { logout } from '@/features/auth/store/authSlice';
import { isSameChatUserId } from '../utils/chatUserId';

/**
 * @typedef {'group'|'direct'} ConversationKind
 */

/**
 * @typedef {Object} ConversationSummary
 * @property {string} id
 * @property {string} name
 * @property {ConversationKind} kind
 * @property {string} [subtitle]
 * @property {string} [lastMessagePreview]
 * @property {string} [lastMessageAt] ISO string
 * @property {number} unreadCount
 * @property {string[]} participantIds
 */

/**
 * @typedef {Object} ChatMessage
 * @property {string} id
 * @property {string} conversationId
 * @property {string} senderId
 * @property {string} senderName
 * @property {string} body
 * @property {string} createdAt ISO string
 * @property {boolean} [isOwn]
 */

const initialState = {
    /** @type {ConversationSummary[]} */
    conversations: [],
    /** Section headings for Groups vs Direct — filled from API/mock */
    sidebarMeta: {
        groupsTitle: 'Group Chat',
        directTitle: 'Specific Chat',
    },
    activeConversationId: null,
    /** @type {Record<string, ChatMessage[]>} */
    messagesByConversation: {},
    isLoadingConversations: false,
    isLoadingMessages: false,
    error: null,
};

export const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        setConversationsLoading(state, action) {
            state.isLoadingConversations = action.payload;
        },
        setMessagesLoading(state, action) {
            state.isLoadingMessages = action.payload;
        },
        setChatError(state, action) {
            state.error = action.payload;
        },
        setConversations(state, action) {
            state.conversations = action.payload;
        },
        setSidebarMeta(state, action) {
            state.sidebarMeta = { ...state.sidebarMeta, ...action.payload };
        },
        upsertConversation(state, action) {
            const next = action.payload;
            const i = state.conversations.findIndex((c) => c.id === next.id);
            if (i >= 0) state.conversations[i] = { ...state.conversations[i], ...next };
            else state.conversations.push(next);
        },
        setActiveConversationId(state, action) {
            state.activeConversationId = action.payload;
        },
        setMessagesForConversation(state, action) {
            const { conversationId, messages } = action.payload;
            state.messagesByConversation[conversationId] = messages;
        },
        addMessage(state, action) {
            const msg = action.payload;
            const { conversationId } = msg;
            if (!state.messagesByConversation[conversationId]) {
                state.messagesByConversation[conversationId] = [];
            }
            state.messagesByConversation[conversationId].push(msg);
            const conv = state.conversations.find((c) => c.id === conversationId);
            if (conv) {
                conv.lastMessagePreview = msg.body.slice(0, 80);
                conv.lastMessageAt = msg.createdAt;
            }
        },
        receiveMessage(state, action) {
            const msg = action.payload;
            const { conversationId } = msg;
            if (!state.messagesByConversation[conversationId]) {
                state.messagesByConversation[conversationId] = [];
            }
            const list = state.messagesByConversation[conversationId];

            // Exact duplicate (same real id) — skip
            if (list.some((m) => m.id === msg.id)) return;

            // Replace optimistic placeholder for our own echo.
            // Match by authorId first (works for entity-scoped msgs where senderId is a pillar/project id),
            // then fall back to senderId for backwards compatibility.
            if (msg.isOwn) {
                const incomingAuthorId = msg.authorId ?? msg.senderId;
                const optIdx = list.findIndex(
                    (m) =>
                        typeof m.id === 'string' &&
                        m.id.startsWith('opt-') &&
                        (isSameChatUserId(m.authorId ?? m.senderId, incomingAuthorId)),
                );
                if (optIdx >= 0) {
                    list[optIdx] = msg;
                    const conv = state.conversations.find((c) => c.id === conversationId);
                    if (conv) {
                        conv.lastMessagePreview = msg.body.slice(0, 80);
                        conv.lastMessageAt = msg.createdAt;
                    }
                    return;
                }
            }

            list.push(msg);
            const conv = state.conversations.find((c) => c.id === conversationId);
            if (conv) {
                conv.lastMessagePreview = msg.body.slice(0, 80);
                conv.lastMessageAt = msg.createdAt;
                // Only count as unread if the user isn't actively viewing this conversation.
                if (!msg.isOwn) {
                    if (state.activeConversationId && conversationId === state.activeConversationId) {
                        conv.unreadCount = 0;
                    } else {
                        conv.unreadCount = (conv.unreadCount || 0) + 1;
                    }
                }
            }
        },
        markConversationRead(state, action) {
            const conversationId = action.payload;
            const conv = state.conversations.find((c) => c.id === conversationId);
            if (conv) conv.unreadCount = 0;
        },
        resetChatState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => initialState);
    },
});

export const {
    setConversationsLoading,
    setMessagesLoading,
    setChatError,
    setConversations,
    setSidebarMeta,
    upsertConversation,
    setActiveConversationId,
    setMessagesForConversation,
    addMessage,
    receiveMessage,
    markConversationRead,
    resetChatState,
} = chatSlice.actions;

export const chatReducer = chatSlice.reducer;
